<script setup lang="ts">
import { ref } from 'vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import TextAreaDefault from '@/components/forms/TextArea/TextAreaDefault.vue'
import { UploadIcon } from '@/icons/'

const emit = defineEmits<{
  (e: 'upload', data: { title: string; description: string; file: File }): void
}>()

const uploadForm = ref({
  title: '',
  description: '',
  file: null as File | null,
})

const handleFileSelect = (event: Event) => {
  const input = event.target as HTMLInputElement
  if (input.files && input.files[0]) {
    const file = input.files[0]

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB')
      input.value = ''
      return
    }

    uploadForm.value.file = file

    if (!uploadForm.value.title) {
      const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '')
      uploadForm.value.title = fileNameWithoutExt
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
    }
  }
}

const validateForm = () => {
  if (!uploadForm.value.title.trim()) {
    alert('Please enter document title')
    return false
  }
  if (!uploadForm.value.file) {
    alert('Please select a file to upload')
    return false
  }
  return true
}

const submitForm = () => {
  if (!validateForm()) return false

  emit('upload', {
    title: uploadForm.value.title,
    description: uploadForm.value.description,
    file: uploadForm.value.file!
  })
  return true
}

const resetForm = () => {
  uploadForm.value = { title: '', description: '', file: null }
}

defineExpose({
  submitForm,
  resetForm
})
</script>

<template>
  <div class="space-y-4">
    <TextInputDefault
      v-model="uploadForm.title"
      labelText="Document Title"
      id="docTitleInput"
      name="docTitleInput"
      type="text"
      placeholder="Enter document title"
      required
    />
    <TextAreaDefault
      v-model="uploadForm.description"
      labelText="Description (Optional)"
      id="docDescriptionInput"
      name="docDescriptionInput"
      placeholder="Enter document description"
      rows="3"
    />

    <div class="space-y-2">
      <label for="docFile" class="block text-sm font-medium text-gray-700">
        Document File <span class="text-red-500">*</span>
      </label>
      <div class="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-blue-400 transition-colors">
        <div class="flex flex-col items-center gap-1">
          <UploadIcon class="w-12 h-12 text-gray-400" />
          <div class="flex text-sm text-gray-600">
            <label for="file-upload" class="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500">
              <span v-if="!uploadForm.file">Upload a file</span>
              <span v-else class="text-green-600">{{ uploadForm.file.name }}</span>
              <input id="file-upload" type="file" class="sr-only" @change="handleFileSelect" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip,.txt" />
            </label>
            <p class="pl-1">or drag and drop</p>
          </div>
          <p class="text-xs text-gray-500">PDF, DOC, XLS, JPG, PNG, ZIP, TXT up to 10MB</p>
        </div>
      </div>
    </div>
  </div>
</template>
